import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError, setBoardingPlank } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Wand2, Mail } from "lucide-react";
import EigooMark from "../components/EigooMark";
import SocialSignInButtons from "../components/SocialSignInButtons";

const roleDestination = (role) => {
    if (role === "director") return "/app/director";
    if (role === "sports_admiral") return "/app/sports-cmdr";
    if (role === "td_commander") return "/app/td-cmdr";
    if (role === "founding_privateer") return "/app/privateer";
    return "/app/map";
};

export default function Login() {
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState("creds"); // creds | mfa | magic
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [useBackup, setUseBackup] = useState(false);
    const [challengeToken, setChallengeToken] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [magicSent, setMagicSent] = useState(null);

    const requestMagicLink = async () => {
        if (!email.trim()) {
            setErr("Enter your email first.");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/auth/magic-link/request", {
                email: email.trim(),
                base_url: window.location.origin,
            });
            setMagicSent(data);
            setStep("magic");
            toast.success("If a Sovereign account exists for that email, a magic link is on its way.");
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message || "Could not request magic link");
        } finally {
            setBusy(false);
        }
    };

    const onSubmitCreds = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.mfa_required) {
                setChallengeToken(data.challenge_token);
                setStep("mfa");
                setCode("");
            } else {
                await refresh();
                toast.success("Welcome aboard, " + (data.name || "Partner"));
                navigate(roleDestination(data.role), { replace: true });
            }
        } catch (ex) {
            // Production-hardened error surface: keep the actual server detail
            // visible instead of swallowing it as "Something went wrong".
            // Director directive 2026.02 — diagnostic transparency on auth.
            const status = ex.response?.status;
            const detail = formatApiError(ex.response?.data?.detail);
            const networkLabel = !ex.response ? "Network or CORS error — backend unreachable." : null;
            setErr(networkLabel || detail || `Login failed (HTTP ${status || "?"}). Try again or contact the Director.`);
        } finally {
            setBusy(false);
        }
    };

    const onSubmitMFA = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/auth/login/mfa", {
                challenge_token: challengeToken,
                code: useBackup ? code.replace(/-/g, "") : code,
                backup: useBackup,
            });
            await refresh();
            toast.success("MFA cleared. Welcome aboard, " + (data.name || data.email));
            navigate(roleDestination(data.role), { replace: true });
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6 grain-overlay relative">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
                <div className="absolute top-[-30%] left-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(40_60%_45%/0.12)] blur-[120px]" />
                <div className="absolute bottom-[-30%] right-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(153_50%_25%/0.25)] blur-[120px]" />
            </div>

            <div className="relative w-full max-w-md glass rounded-2xl p-8 md:p-10">
                <EigooMark size="md" showWord={true} testid="auth-logo" className="mb-1" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-3 mb-8">
                    {step === "mfa" ? "Multi-Factor Verification" : "Silent Partner Login"}
                </div>

                {step === "creds" && (
                    <form onSubmit={onSubmitCreds} className="space-y-4" data-testid="login-creds-form">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email"
                                className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors"
                                placeholder="director@eigoo.com"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password"
                                className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors"
                            />
                        </div>

                        {err && (
                            <div className="text-sm text-[hsl(350_80%_70%)] bg-[hsl(350_60%_20%/0.4)] border border-[hsl(350_60%_40%/0.3)] rounded-md px-3 py-2" data-testid="login-error">
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="login-submit"
                            className="w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                        >
                            {busy ? "Boarding..." : "Sign In"}
                        </button>

                        {/* Director Magic Link · passwordless rail (Director directive 2026.02) */}
                        <div className="pt-3 border-t border-[hsl(40_40%_30%/0.18)]">
                            {/* Social sign-in row · Strike #19. Drops in
                                ABOVE the magic-link button so it's the
                                first eye-catch after password fails. */}
                            <SocialSignInButtons testidPrefix="login-social" />

                            <div className="mt-4 pt-4 border-t border-[hsl(40_40%_30%/0.18)]" />

                            <button
                                type="button"
                                onClick={requestMagicLink}
                                disabled={busy || !email.trim()}
                                data-testid="login-magic-link-request"
                                className="w-full py-2.5 rounded-md bg-transparent border border-[hsl(40_50%_35%/0.5)] text-foreground/85 font-medium hover:border-[hsl(45_85%_68%)] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2 text-sm"
                            >
                                <Wand2 size={14} className="text-[hsl(45_85%_68%)]" />
                                Email me a magic link instead
                            </button>
                            <div className="text-[10px] text-muted-foreground text-center mt-2">
                                For Directors + Admirals · 15-minute single-use link · bypasses MFA
                            </div>

                            {/* 🚨 Presidential Boarding Plank — Director-only emergency vector */}
                            <button
                                type="button"
                                disabled={busy || !email.trim() || !password}
                                data-testid="login-boarding-plank"
                                onClick={async () => {
                                    setErr("");
                                    setBusy(true);
                                    try {
                                        // Arm the plank credential (sessionStorage) — every
                                        // subsequent API call will carry the X-Director-Plank
                                        // headers so auth survives a missing cookie.
                                        setBoardingPlank({ email: email.trim().toLowerCase(), password });
                                        // Probe /auth/me using the plank to confirm the
                                        // Director identity loaded server-side, then route.
                                        const { data } = await api.get("/auth/me");
                                        if (data?.role === "director" || ["director@eigoo.com","sales@bizboogie.com"].includes((data?.email||"").toLowerCase())) {
                                            await refresh();
                                            toast.success(`Boarding Plank armed for ${data.email}.`);
                                            navigate(roleDestination("director"));
                                        } else {
                                            throw new Error("Plank unavailable — not a Director coordinate.");
                                        }
                                    } catch (e) {
                                        setBoardingPlank({ email: "", password: "" }); // clear on failure
                                        setErr(e?.response?.data?.detail || e.message || "Plank rejected.");
                                    } finally {
                                        setBusy(false);
                                    }
                                }}
                                className="mt-3 w-full py-2.5 rounded-md bg-transparent border border-[hsl(350_55%_45%/0.45)] text-[hsl(350_75%_75%)] font-medium hover:border-[hsl(350_85%_70%)] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2 text-sm"
                            >
                                <ShieldCheck size={14} />
                                Boarding Plank · Director only
                            </button>
                            <div className="text-[10px] text-muted-foreground text-center mt-1.5">
                                Emergency vector · header-auth · bypasses cookie/MFA · time-bombed
                            </div>
                        </div>
                    </form>
                )}

                {step === "magic" && (
                    <div className="space-y-4 text-center" data-testid="login-magic-sent">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[hsl(153_50%_25%)] border border-[hsl(153_50%_50%/0.5)]">
                            <Mail size={22} className="text-[hsl(153_60%_70%)]" />
                        </div>
                        <div className="font-heading text-2xl">Check your inbox</div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            If a Sovereign account exists for <span className="font-mono text-foreground">{email}</span>,
                            a magic link has been dispatched. The link expires in <strong>{magicSent?.expires_in_minutes || 15} minutes</strong> and burns on first click.
                        </p>
                        {magicSent?._dev_link && (
                            <div className="mt-4 p-3 rounded-lg border border-dashed border-[hsl(45_85%_50%/0.4)] bg-[hsl(40_40%_15%/0.3)] text-left">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(45_85%_68%)] mb-1">
                                    Dev link · Resend domain unverified
                                </div>
                                <a
                                    href={magicSent._dev_link}
                                    data-testid="login-magic-dev-link"
                                    className="text-[11px] font-mono text-[hsl(45_85%_75%)] hover:text-[hsl(45_95%_85%)] break-all"
                                >
                                    {magicSent._dev_link}
                                </a>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => { setStep("creds"); setMagicSent(null); }}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-3"
                        >
                            ← Back to password sign-in
                        </button>
                    </div>
                )}

                {step === "mfa" && (
                    <form onSubmit={onSubmitMFA} className="space-y-4" data-testid="login-mfa-form">
                        <div className="flex items-center gap-2 text-[hsl(45_85%_68%)] text-sm mb-2">
                            <ShieldCheck className="w-4 h-4" />
                            <span>Enter the {useBackup ? "10-digit backup code" : "6-digit code from your authenticator"}</span>
                        </div>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={useBackup ? 11 : 6}
                            value={code}
                            onChange={(e) => setCode(useBackup ? e.target.value.replace(/[^\d-]/g, "") : e.target.value.replace(/\D/g, ""))}
                            required
                            autoFocus
                            data-testid="login-mfa-code"
                            placeholder={useBackup ? "XXXXX-XXXXX" : "000000"}
                            className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors font-mono tracking-[0.4em] text-center"
                        />

                        {err && (
                            <div className="text-sm text-[hsl(350_80%_70%)] bg-[hsl(350_60%_20%/0.4)] border border-[hsl(350_60%_40%/0.3)] rounded-md px-3 py-2" data-testid="login-mfa-error">
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="login-mfa-submit"
                            className="w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                        >
                            {busy ? "Verifying..." : "Complete Sign-In"}
                        </button>

                        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                            <button
                                type="button"
                                onClick={() => { setUseBackup(!useBackup); setCode(""); setErr(""); }}
                                data-testid="login-mfa-toggle-backup"
                                className="flex items-center gap-1 hover:text-[hsl(45_85%_68%)] transition-colors"
                            >
                                <KeyRound className="w-3 h-3" />
                                {useBackup ? "Use authenticator" : "Use backup code"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setStep("creds"); setErr(""); setCode(""); setChallengeToken(null); }}
                                data-testid="login-mfa-back"
                                className="hover:text-[hsl(45_85%_68%)] transition-colors"
                            >
                                ← Restart
                            </button>
                        </div>
                    </form>
                )}

                <div className="mt-6 text-center text-sm text-muted-foreground">
                    New agency?{" "}
                    <Link to="/register" className="text-[hsl(45_85%_68%)] hover:underline" data-testid="login-register-link">
                        Board the Fleet
                    </Link>
                </div>

                <div className="mt-8 pt-6 border-t border-[hsl(40_40%_25%/0.2)] text-[11px] text-muted-foreground">
                    <div className="uppercase tracking-[0.3em] mb-2">Director demo</div>
                    <div className="font-mono">director@eigoo.com / EigooDir2025!</div>
                </div>
            </div>
        </div>
    );
}
